import styled from 'styled-components';

import { TabTypeValue } from 'components/patient/concurrentReview/ConcurrentReviewSlideout';
import DividerSeries from 'components/shared/DividerSeries';
import { Flags } from 'constants/flags';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { simpleDate } from 'lib/date';
import { PERMISSIONS } from 'models/Profile';
import Authorization from 'models/reviews/Authorization';
import { colors } from 'styles/theme/colors';
import { BodySmallBold, Label } from 'styles/typography';
import ChevronRightBold from 'svg/ChevronRightBold';

const HistoryItemLink = ({ onClick, ...props }: { onClick?: () => void } & React.ComponentProps<typeof Link>) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    onClick?.();
  };

  return <Link onClick={handleClick} {...props} />;
};

type ConcurrentReviewHistoryListProps = {
  history: Authorization[];
  onViewAuthorization: (authorization: Authorization, initialTab?: TabTypeValue) => void;
};

export default function ConcurrentReviewHistoryList({
  history,
  onViewAuthorization,
}: ConcurrentReviewHistoryListProps) {
  const { profile } = useProfile();
  const { episode } = useEpisode();
  const { locationEpisode } = useSelectedLocationEpisode();
  const locationEpisodeMutable = !locationEpisode?.archived && !locationEpisode?.discharged && !episode?.archived;
  const canCreateSummary =
    locationEpisodeMutable && profile?.has(PERMISSIONS.aiJobSummaryCreate, { episode, locationEpisode });
  const canCreateComparison =
    locationEpisodeMutable && profile?.has(PERMISSIONS.aiJobComparisonCreate, { episode, locationEpisode });

  return (
    <HistoryListContainer>
      {history.length === 0 && (
        <HistoryListItem>
          <EmptyStateText>No concurrent reviews to show yet</EmptyStateText>
        </HistoryListItem>
      )}
      {history.map((item, index) => (
        <HistoryListItem key={item.id} onClick={() => onViewAuthorization(item, item.summary ? 'summary' : 'files')}>
          <HistoryListItemInfo>
            <HeaderRow>
              Approval Through {item.data.approvedThroughDate ? simpleDate(item.data.approvedThroughDate) : 'N/A'}
            </HeaderRow>
            <InfoRow>
              <DividerSeries divider={<Dot />}>
                <span>{item.attachmentCount ?? item.attachments.length} files</span>
                {item.summary
                  ? 'Summarized'
                  : canCreateSummary &&
                    (item.attachmentCount ?? item.attachments.length) > 0 && (
                      <HistoryItemLink onClick={() => onViewAuthorization(item, 'summary')}>
                        Summarize Files
                      </HistoryItemLink>
                    )}
                {profile.hasFlag(Flags.ComparisonForAiSummary) && item.summary && index < history.length - 1 && (
                  <>
                    {item.comparison ? (
                      // TODO: can we pull this info off the comparison artifact itself?
                      <span>
                        Compared to{' '}
                        {history[index + 1].data.approvedThroughDate
                          ? simpleDate(history[index + 1].data.approvedThroughDate)
                          : 'N/A'}
                      </span>
                    ) : (
                      canCreateComparison &&
                      history[index + 1].summary && (
                        <HistoryItemLink onClick={() => onViewAuthorization(item, 'comparison')}>
                          Compare to{' '}
                          {history[index + 1].data.approvedThroughDate
                            ? simpleDate(history[index + 1].data.approvedThroughDate)
                            : 'N/A'}
                        </HistoryItemLink>
                      )
                    )}
                  </>
                )}
              </DividerSeries>
            </InfoRow>
          </HistoryListItemInfo>
          <ChevronContainer>
            <ChevronRightBold width={10} height={10} fill={colors.black100} />
          </ChevronContainer>
        </HistoryListItem>
      ))}
    </HistoryListContainer>
  );
}

const HistoryListContainer = styled.div`
  background-color: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  & > div:not(:last-child) {
    border-bottom: 1px solid ${colors.black10};
  }
`;

const HistoryListItem = styled.div<{ onClick?: () => void }>`
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${({ onClick }) =>
    onClick &&
    `
      cursor: pointer;
    `}
`;

const HistoryListItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeaderRow = styled(BodySmallBold)`
  line-height: 14px;
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${colors.black25};
`;

const InfoRow = styled(Label)`
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 12px;
  color: ${colors.black50};
`;

const EmptyStateText = styled(Label)`
  color: ${colors.black50};
  text-align: center;
  width: 100%;
`;

const Link = styled.a`
  font-size: 12px;
  line-height: 12px;
  color: ${colors.purple100};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const ChevronContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0.5;
  height: 100%;
  cursor: pointer;
`;
